import { useEffect, useState } from 'react';
import {
  AtSign,
  ChevronsUpDown,
  Check,
  Landmark,
  Loader2,
  MapPin,
  Plus,
  StickyNote,
  User,
  type LucideIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/atoms/button';
import { Command, CommandInput, CommandList } from '@/components/atoms/command';
import { Input } from '@/components/atoms/input';
import { Label } from '@/components/atoms/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/atoms/popover';
import { Skeleton } from '@/components/atoms/skeleton';
import { Switch } from '@/components/atoms/switch';
import { Textarea } from '@/components/atoms/textarea';
import { AddressFields } from '@/components/molecules';
import { useCreateBillTo, useUpdateBillTo, type UpsertBillToDto } from '@/hooks/contacts/useBillTo';
import { useContact } from '@/hooks/contacts/useContact';
import { useContactSearch } from '@/hooks/contacts/useContactSearch';
import { getContactDisplayName } from '@/lib/contacts';
import type { BillTo, BillToOverrideAddress, Contact } from '@/types/contact';

const FORM_ID = 'contact-bill-to-form';

interface BillToFormState {
  label: string;
  contactId: string;
  isDefault: boolean;
  overrideEmail: string;
  notes: string;
  overrideAddress: {
    street1: string;
    street2: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
  };
}

interface OptionalSectionsState {
  email: boolean;
  address: boolean;
  notes: boolean;
}

function getContactName(contact?: Contact | null) {
  if (!contact) return 'Contact';
  return getContactDisplayName(contact);
}

function getInitialForm(
  billTo: BillTo | null | undefined,
  fallbackContactId: string,
  fallbackDefault: boolean,
): BillToFormState {
  const overrideAddress = billTo?.overrideAddress;

  return {
    label: billTo?.label ?? '',
    contactId: billTo?.contactId ?? fallbackContactId,
    isDefault: billTo?.isDefault ?? fallbackDefault,
    overrideEmail: billTo?.overrideEmail ?? '',
    notes: billTo?.notes ?? '',
    overrideAddress: {
      street1: overrideAddress?.street1 ?? '',
      street2: overrideAddress?.street2 ?? '',
      city: overrideAddress?.city ?? '',
      state: overrideAddress?.state ?? '',
      postalCode: overrideAddress?.postalCode ?? '',
      country: overrideAddress?.country ?? 'US',
    },
  };
}

function buildOverrideAddressPayload(form: BillToFormState): BillToOverrideAddress | null {
  const payload = {
    street1: form.overrideAddress.street1.trim() || null,
    street2: form.overrideAddress.street2.trim() || null,
    city: form.overrideAddress.city.trim() || null,
    state: form.overrideAddress.state.trim() || null,
    postalCode: form.overrideAddress.postalCode.trim() || null,
    country: form.overrideAddress.country.trim() || null,
  };

  return Object.values(payload).some(Boolean) ? payload : null;
}

function hasOverrideAddress(address: BillToOverrideAddress | null | undefined): boolean {
  if (!address) return false;
  return Boolean(
    address.street1 || address.street2 || address.city || address.state || address.postalCode,
  );
}

function getInitialOptionalSections(billTo: BillTo | null | undefined): OptionalSectionsState {
  return {
    email: Boolean(billTo?.overrideEmail),
    address: hasOverrideAddress(billTo?.overrideAddress),
    notes: Boolean(billTo?.notes),
  };
}

function buildPayload(form: BillToFormState): UpsertBillToDto {
  return {
    label: form.label.trim(),
    contactId: form.contactId,
    isDefault: form.isDefault,
    overrideEmail: form.overrideEmail.trim() || null,
    overrideAddress: buildOverrideAddressPayload(form),
    notes: form.notes.trim() || null,
  };
}

/**
 * Bill-to form fields + footer with no surrounding layout — shared by the
 * slide-over sheet ({@link ContactBillToFormSheet}) and the deprecated
 * full-page route ({@link ContactBillToFormPanel}).
 */
export function ContactBillToFormBody({
  mode,
  contactId,
  currentContact,
  billTo,
  entriesCount = 0,
  isLoading,
  onCancel,
  onSaved,
}: {
  mode: 'create' | 'edit';
  contactId: string;
  currentContact?: Contact | null;
  billTo?: BillTo | null;
  entriesCount?: number;
  isLoading?: boolean;
  onCancel: () => void;
  onSaved?: () => void;
}) {
  const createBillTo = useCreateBillTo(contactId);
  const updateBillTo = useUpdateBillTo(contactId);
  const [form, setForm] = useState<BillToFormState>(() =>
    getInitialForm(billTo, contactId, entriesCount === 0),
  );
  const [sections, setSections] = useState<OptionalSectionsState>(() =>
    getInitialOptionalSections(billTo),
  );
  const [contactPickerOpen, setContactPickerOpen] = useState(false);
  const [contactSearch, setContactSearch] = useState('');
  const [selectedContactLabel, setSelectedContactLabel] = useState<string | null>(null);

  const selectedRelatedContactId =
    form.contactId && form.contactId !== contactId ? form.contactId : '';
  const selectedRelatedContactQuery = useContact(selectedRelatedContactId);
  const contactSearchQuery = useContactSearch(contactSearch, 10, {
    enabled: contactPickerOpen && contactSearch.trim().length >= 2,
  });

  useEffect(() => {
    setForm(getInitialForm(billTo, contactId, entriesCount === 0));
    setSections(getInitialOptionalSections(billTo));
    setSelectedContactLabel(null);
  }, [billTo, contactId, entriesCount]);

  function set<K extends keyof BillToFormState>(key: K, value: BillToFormState[K]) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  function patchOverrideAddress(next: Partial<BillToFormState['overrideAddress']>) {
    setForm((current) => ({
      ...current,
      overrideAddress: { ...current.overrideAddress, ...next },
    }));
  }

  function showSection(key: keyof OptionalSectionsState) {
    setSections((current) => ({ ...current, [key]: true }));
  }

  function selectOwnerContact() {
    set('contactId', contactId);
    setSelectedContactLabel(null);
    setContactPickerOpen(false);
  }

  function selectRelatedContact(id: string, label: string) {
    set('contactId', id);
    setSelectedContactLabel(label);
    setContactPickerOpen(false);
  }

  const searchResults = (contactSearchQuery.data?.data ?? []).filter(
    (contact) => contact.id !== contactId,
  );
  const selectedContact =
    form.contactId === contactId ? currentContact : selectedRelatedContactQuery.data;
  const selectedContactName =
    selectedContactLabel ??
    (form.contactId === contactId
      ? 'Self — bill the customer'
      : selectedContact
        ? getContactName(selectedContact)
        : form.contactId
          ? 'Loading contact...'
          : 'Select contact');
  const labelComplete = Boolean(form.label.trim());
  const contactComplete = Boolean(form.contactId);
  const isSubmitting = createBillTo.isPending || updateBillTo.isPending;

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (!labelComplete) {
      toast.error('Label is required');
      return;
    }
    if (!contactComplete) {
      toast.error('Bill-to contact is required');
      return;
    }

    try {
      const payload = buildPayload(form);
      if (mode === 'edit' && billTo) {
        await updateBillTo.mutateAsync({ billToId: billTo.id, ...payload });
      } else {
        await createBillTo.mutateAsync(payload);
      }
      onSaved?.();
    } catch {
      // Mutation hooks surface API errors.
    }
  }

  if (isLoading || (mode === 'edit' && !billTo)) {
    return (
      <div className="flex min-h-0 flex-1 flex-col">
        <div className="flex-1 space-y-4 overflow-y-auto px-4 py-4">
          <Skeleton className="h-28 w-full rounded-xl" />
          <Skeleton className="h-36 w-full rounded-xl" />
          <Skeleton className="h-28 w-full rounded-xl" />
        </div>
        <div className="flex shrink-0 items-center justify-end gap-2 border-t px-4 py-3">
          <Button type="button" size="sm" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <form
        id={FORM_ID}
        onSubmit={handleSubmit}
        className="min-w-0 flex-1 overflow-y-auto bg-muted/40"
      >
        <div className="space-y-4 px-4 py-4">
          <section className="rounded-xl border bg-background p-4 shadow-xs">
            <div className="mb-4 flex items-center justify-between gap-3">
              <h2 className="text-base font-semibold text-foreground">Billing relationship</h2>
              <span className="rounded-full bg-foreground px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-background">
                Required
              </span>
            </div>

            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="bill-to-label">Label</Label>
                <Input
                  id="bill-to-label"
                  value={form.label}
                  onChange={(event) => set('label', event.target.value)}
                  placeholder="Property manager"
                  className="bg-background"
                  required
                />
              </div>

              <div className="space-y-1.5">
                <Label>Bill-to contact</Label>
                <Popover open={contactPickerOpen} onOpenChange={setContactPickerOpen}>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className="flex h-10 w-full items-center gap-2 rounded-md border border-input bg-background px-3 text-sm transition-colors hover:bg-muted/50"
                    >
                      <span className="min-w-0 flex-1 truncate text-left">
                        {selectedContactName}
                      </span>
                      <ChevronsUpDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[360px] p-0" align="start">
                    <Command>
                      <CommandInput
                        placeholder="Search contacts..."
                        value={contactSearch}
                        onValueChange={setContactSearch}
                      />
                      <CommandList>
                        {currentContact ? (
                          <div className="border-b px-1 py-1">
                            <ScopedBillToContactRow
                              active={form.contactId === contactId && selectedContactLabel === null}
                              icon={Landmark}
                              title="Self — bill the customer"
                              subtitle={
                                currentContact.primaryEmail ?? getContactName(currentContact)
                              }
                              onSelect={selectOwnerContact}
                            />
                          </div>
                        ) : null}

                        {contactSearch.trim().length < 2 ? (
                          <div className="px-3 py-6 text-sm text-muted-foreground">
                            Type at least 2 characters to search other contacts.
                          </div>
                        ) : contactSearchQuery.isLoading ? (
                          <div className="flex items-center gap-2 px-3 py-6 text-sm text-muted-foreground">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Searching contacts...
                          </div>
                        ) : searchResults.length === 0 ? (
                          <div className="px-3 py-6 text-sm text-muted-foreground">
                            No matching contacts.
                          </div>
                        ) : (
                          <div className="max-h-72 overflow-y-auto py-1">
                            {searchResults.map((contact) => (
                              <ScopedBillToContactRow
                                key={contact.id}
                                active={form.contactId === contact.id}
                                icon={User}
                                title={contact.displayName}
                                subtitle={contact.primaryEmail ?? 'Contact'}
                                onSelect={() =>
                                  selectRelatedContact(contact.id, contact.displayName)
                                }
                              />
                            ))}
                          </div>
                        )}
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
                <p className="text-xs text-muted-foreground">
                  The customer or another customer record.
                </p>
              </div>

              <div className="flex items-center justify-between gap-3 rounded-lg border bg-muted/60 p-3">
                <div className="min-w-0">
                  <Label htmlFor="bill-to-default" className="text-sm font-medium">
                    Default billing destination
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Use this destination by default for invoices and statements.
                  </p>
                </div>
                <Switch
                  id="bill-to-default"
                  checked={form.isDefault}
                  onCheckedChange={(checked) => set('isDefault', checked)}
                />
              </div>
            </div>
          </section>

          {(!sections.email || !sections.address || !sections.notes) && (
            <div className="space-y-2">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Add if needed
              </p>
              <div className="flex flex-wrap gap-2">
                {!sections.email ? (
                  <OptionalFieldChip
                    icon={AtSign}
                    label="Override email"
                    onClick={() => showSection('email')}
                  />
                ) : null}
                {!sections.address ? (
                  <OptionalFieldChip
                    icon={MapPin}
                    label="Override address"
                    onClick={() => showSection('address')}
                  />
                ) : null}
                {!sections.notes ? (
                  <OptionalFieldChip
                    icon={StickyNote}
                    label="Notes"
                    onClick={() => showSection('notes')}
                  />
                ) : null}
              </div>
            </div>
          )}

          {sections.email ? (
            <section className="rounded-xl border bg-background p-4 shadow-xs">
              <div className="space-y-1.5">
                <Label htmlFor="bill-to-email">Override email</Label>
                <Input
                  id="bill-to-email"
                  type="email"
                  value={form.overrideEmail}
                  onChange={(event) => set('overrideEmail', event.target.value)}
                  placeholder="billing@example.com"
                  className="bg-background"
                />
              </div>
            </section>
          ) : null}

          {sections.address ? (
            <section className="rounded-xl border bg-background p-4 shadow-xs">
              <div className="mb-3 flex items-center gap-2">
                <MapPin className="h-4 w-4 text-muted-foreground" />
                <h2 className="text-sm font-semibold text-foreground">Override address</h2>
              </div>
              <AddressFields
                value={form.overrideAddress}
                onChange={patchOverrideAddress}
                idPrefix="bill-to"
              />
            </section>
          ) : null}

          {sections.notes ? (
            <section className="rounded-xl border bg-background p-4 shadow-xs">
              <div className="space-y-1.5">
                <Label htmlFor="bill-to-notes">Notes</Label>
                <Textarea
                  id="bill-to-notes"
                  rows={4}
                  value={form.notes}
                  onChange={(event) => set('notes', event.target.value)}
                  placeholder="Internal notes for this billing relationship"
                  className="bg-background"
                />
              </div>
            </section>
          ) : null}
        </div>
      </form>

      <div className="flex shrink-0 items-center justify-end gap-2 border-t bg-background px-4 py-3">
        <Button type="button" size="sm" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          type="submit"
          size="sm"
          form={FORM_ID}
          disabled={!labelComplete || !contactComplete || isSubmitting}
        >
          {isSubmitting && <Loader2 size={14} className="mr-1.5 animate-spin" />}
          {mode === 'edit' ? 'Save' : 'Create'}
        </Button>
      </div>
    </div>
  );
}

function OptionalFieldChip({
  icon: Icon,
  label,
  onClick,
}: {
  icon: LucideIcon;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex h-9 items-center gap-2 rounded-full border bg-background px-3 text-sm font-medium text-foreground shadow-xs transition-colors hover:bg-muted/60"
    >
      <Plus className="h-3.5 w-3.5 text-muted-foreground" />
      <Icon className="h-3.5 w-3.5 text-muted-foreground" />
      {label}
    </button>
  );
}

function ScopedBillToContactRow({
  active = false,
  icon: Icon,
  title,
  subtitle,
  onSelect,
}: {
  active?: boolean;
  icon: LucideIcon;
  title: string;
  subtitle?: string;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`flex w-full items-center gap-3 rounded-md p-2.5 text-left text-sm transition-colors ${
        active ? 'bg-primary/10 text-primary' : 'hover:bg-muted'
      }`}
    >
      <Icon className="h-3.5 w-3.5 shrink-0" />
      <span className="min-w-0 flex-1">
        <span className="block truncate font-medium">{title}</span>
        {subtitle ? (
          <span className="block truncate text-xs text-muted-foreground">{subtitle}</span>
        ) : null}
      </span>
      {active ? <Check className="h-3.5 w-3.5 shrink-0" /> : null}
    </button>
  );
}
